/** Client-side host normalization for OpenVAS (mirrors backend/lib/gvm-hosts.js). */

const IPV4_RE =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?!$)|$)){4}$/

const HOSTNAME_RE =
  /^(?=.{1,253}$)(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/

const PLACEHOLDERS = new Set(['', 'n/a', 'na', '-', 'none', 'unknown', 'tbd'])

export function normalizeScanHost(raw: unknown): string | null {
  let s = String(raw ?? '').trim()
  if (!s) return null

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s)
      s = u.hostname || s
    } catch {
      s = s.replace(/^https?:\/\//i, '').split('/')[0]
    }
  }

  if (!/^\[.+\]$/.test(s) && s.includes(':') && !s.includes('/')) {
    const idx = s.lastIndexOf(':')
    const hostPart = s.slice(0, idx)
    const portPart = s.slice(idx + 1)
    if (/^\d+$/.test(portPart) && (IPV4_RE.test(hostPart) || HOSTNAME_RE.test(hostPart))) {
      s = hostPart
    }
  }

  s = s.split('/')[0].trim()
  if (PLACEHOLDERS.has(s.toLowerCase())) return null
  if (IPV4_RE.test(s)) return s
  if (/\s/.test(s) || /[^\x20-\x7E]/.test(s)) return null
  const lower = s.toLowerCase()
  if (HOSTNAME_RE.test(lower)) return lower
  return null
}

type AssetLike = {
  ipAddress?: string
  name?: string
  type?: string
  scanHost?: string
  details?: Record<string, unknown>
}

/** Merge asset.details with lazy-loaded /details API payload. */
export function mergeAssetDetailSources(
  asset: AssetLike,
  detailRecord?: Record<string, unknown>,
): Record<string, unknown> {
  const base =
    asset.details && typeof asset.details === 'object'
      ? { ...asset.details }
      : {}
  const extra = detailRecord && typeof detailRecord === 'object' ? detailRecord : {}
  if (extra.asset && typeof extra.asset === 'object') {
    return { ...base, ...(extra.asset as Record<string, unknown>), ...extra }
  }
  return { ...base, ...extra }
}

export function resolveAssetScanHost(
  asset: AssetLike,
  detailRecord?: Record<string, unknown>,
): string {
  if (asset.scanHost) {
    const fromProp = normalizeScanHost(asset.scanHost)
    if (fromProp) return fromProp
  }

  const d = mergeAssetDetailSources(asset, detailRecord)
  const type = String(asset.type ?? d.type ?? d.assetType ?? '').toUpperCase()
  const candidates: unknown[] = [
    asset.ipAddress,
    d.ip,
    d.ipAddress,
    d.hostname,
    d.host,
    d.fqdn,
    d.domain,
    d.url,
  ]
  if (type === 'IP' || type === 'HOSTNAME') {
    candidates.unshift(asset.name)
  }
  for (const c of candidates) {
    const host = normalizeScanHost(c)
    if (host) return host
  }
  return normalizeScanHost(asset.name) || ''
}

/** Human-readable IP/hostname/URL line for inventory cards and tables. */
export function formatAssetTargetLabel(
  asset: AssetLike & { details?: Record<string, unknown> },
): string {
  const host = resolveAssetScanHost(asset, asset.details)
  if (host) return host
  const d = asset.details || {}
  const parts = [d.ip, d.hostname, d.url].filter((x) => x != null && String(x).trim())
  return parts.length ? parts.map((x) => String(x).trim()).join(' · ') : ''
}
